import { jsx as _jsx } from "react/jsx-runtime";
import { createRootRouteWithContext, createRoute, Outlet, redirect, } from '@tanstack/react-router';
import { z } from 'zod';
import Loading from 'components/Loading';
import LoginScreen from 'features/auth/screens/LoginScreen';
import ResetPasswordScreen from 'features/auth/screens/ResetPasswordScreen';
import VerifyEmailScreen from 'features/auth/screens/VerifyEmailScreen';
import BlockedScreen from 'features/auth/screens/BlockedScreen';
import AuthActionScreen from 'features/auth/screens/AuthActionScreen';
import ProjectsScreen from 'components/ProjectsScreen';
// ---- Root ----
export const rootRoute = createRootRouteWithContext()({
    beforeLoad: ({ context }) => context.auth.whenReady(),
    component: () => _jsx(Outlet, {}),
});
// ---- / (index) ----
const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'email-unverified')
            throw redirect({ to: '/verify-email' });
        if (s.kind === 'blocked')
            throw redirect({ to: '/blocked', search: { reason: s.reason } });
        if (s.kind === 'authenticated')
            throw redirect({ to: '/projects' });
    },
});
// ---- /auth/action ----
const authActionSearchSchema = z.object({
    mode: z.enum(['verifyEmail', 'resetPassword']),
    oobCode: z.string().min(1),
});
const authActionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/auth/action',
    validateSearch: authActionSearchSchema,
    component: function AuthAction() {
        const { mode, oobCode } = authActionRoute.useSearch();
        return _jsx(AuthActionScreen, { mode: mode, oobCode: oobCode });
    },
});
// ---- /blocked ----
const blockedSearchSchema = z.object({
    reason: z.enum(['deleted', 'inactive', 'unsupported-account', 'exchange-failed']).optional(),
});
const blockedRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/blocked',
    validateSearch: blockedSearchSchema,
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'authenticated' || s.kind === 'email-unverified')
            throw redirect({ to: '/' });
        // blocked (with or without a user) renders the standalone screen — Vision has no
        // in-shell blocked mode.
    },
    component: function Blocked() {
        const { reason } = blockedRoute.useSearch();
        return _jsx(BlockedScreen, { reason: (reason || 'exchange-failed') });
    },
});
// ---- /verify-email ----
const verifyEmailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/verify-email',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'blocked')
            throw redirect({ to: '/blocked', search: { reason: s.reason } });
        if (s.kind === 'authenticated')
            throw redirect({ to: '/' });
        // email-unverified — render
    },
    component: VerifyEmailScreen,
});
// ---- Public layout (authenticated users bounce to /) ----
const publicLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'public',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'authenticated' || s.kind === 'email-unverified') {
            throw redirect({ to: '/' });
        }
        if (s.kind === 'blocked') {
            throw redirect({ to: '/blocked', search: { reason: s.reason } });
        }
    },
    component: () => _jsx(Outlet, {}),
});
const loginRoute = createRoute({
    getParentRoute: () => publicLayoutRoute,
    path: '/login',
    component: LoginScreen,
});
const resetPasswordSearchSchema = z.object({
    code: z.string().min(1),
});
const resetPasswordRoute = createRoute({
    getParentRoute: () => publicLayoutRoute,
    path: '/reset-password',
    validateSearch: resetPasswordSearchSchema,
    component: function ResetPassword() {
        const { code } = resetPasswordRoute.useSearch();
        return _jsx(ResetPasswordScreen, { code: code });
    },
});
// ---- Authenticated layout ----
const authenticatedLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'authenticated',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'email-unverified')
            throw redirect({ to: '/verify-email' });
        if (s.kind === 'blocked')
            throw redirect({ to: '/blocked', search: { reason: s.reason } });
        if (s.kind === 'authenticated')
            return { user: s.user };
        // loading — let the component render; __root.beforeLoad already awaits whenReady
    },
    component: function AuthenticatedLayout() {
        const ctx = authenticatedLayoutRoute.useRouteContext();
        if (!ctx.user)
            return _jsx(Loading, { isVisible: true, title: "" });
        return _jsx(Outlet, {});
    },
});
const projectsRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/projects',
    component: function Projects() {
        const ctx = authenticatedLayoutRoute.useRouteContext();
        if (!ctx.user)
            return null;
        return _jsx(ProjectsScreen, { user: ctx.user });
    },
});
// ---- Assemble ----
export const routeTree = rootRoute.addChildren([
    indexRoute,
    authActionRoute,
    blockedRoute,
    verifyEmailRoute,
    publicLayoutRoute.addChildren([loginRoute, resetPasswordRoute]),
    authenticatedLayoutRoute.addChildren([projectsRoute]),
]);
